import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  BaseService,
  CentrifugoService,
  Game,
  GameStatuses,
  LeagueApi,
  TournamentApi,
  TournamentGamesFilters,
  TournamentSeasonApi
} from '@mtgame/core';
import { Observable } from 'rxjs';
import { EXCLUDE_GAMES_TOURNAMENT_ID, EXCLUDE_HEADLINE_GAMES } from '@utils/hardcode';
import { isPlatformBrowser } from '@angular/common';

const STORE_GAMES_KEY = 'games';

@Injectable({providedIn: 'root'})
export class GamesHeadlineService extends BaseService {
  private openGamesDateTo: Date;

  size = 30;
  openGamesPage = 1;
  closedGamesPage = 0;
  openGamesTotal: number;
  closedGamesTotal: number;
  tournamentId: number;
  leagueId: number;
  tournamentSeasonId: number;

  get games$(): Observable<Game[]> {
    return this.store.get(STORE_GAMES_KEY);
  }

  constructor(
    private leagueApi: LeagueApi,
    private tournamentApi: TournamentApi,
    private tournamentSeasonApi: TournamentSeasonApi,
    private centrifugoService: CentrifugoService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    super();
  }

  async initialize({leagueId, tournamentSeasonId, tournamentId}:
      {leagueId?: number, tournamentSeasonId?: number, tournamentId?: number}
  ): Promise<Game[]> {
    this.openGamesPage = 1;
    this.closedGamesPage = 0;
    this.leagueId = leagueId;
    this.tournamentSeasonId = tournamentSeasonId;
    this.tournamentId = tournamentId;
    this.listenCentrifugoEvents();
    return await this.loadGames();
  }

  dispose(): void {
    this.store.save(STORE_GAMES_KEY, null);
    super.dispose();
  }

  async loadGames(): Promise<Game[]> {
    if (this.leagueId !== 34) {
      this.closedGamesPage = 1;
      return await this.loadClosedGames();
    }
    const filters: TournamentGamesFilters = {};
    if (this.tournamentId) {
      filters.tournamentIds = [this.tournamentId];
    }
    if (this.tournamentSeasonId) {
      filters.tournamentSeasonId = this.tournamentSeasonId;
    }
    const [ openGame, closedGame ] = await Promise.all([
      this.getFirstGame({...filters, statuses: [GameStatuses.open]}),
      this.getFirstGame({...filters, statuses: [GameStatuses.in_progress, GameStatuses.closed]})
    ]);
    const today = new Date();
    today.setHours(23, 59, 59);
    if (openGame && closedGame) {
      const openGameDelta = Math.abs(openGame.datetime.getTime() - today.getTime());
      const closedGameDelta = Math.abs(today.getTime() - closedGame.datetime.getTime());
      if (openGameDelta > closedGameDelta) {
        this.openGamesTotal = 0;
        this.closedGamesPage = 1;
        return await this.loadClosedGames();
      }
    }
    if (openGame) {
      this.openGamesDateTo = new Date(openGame.datetime.getTime() + 1000 * 3600 * 24 * 6);
      return await this.loadOpenGames();
    }
    this.closedGamesPage = 1;
    return await this.loadClosedGames();
  }

  async loadOpenGames(): Promise<Game[]> {
    const filters: TournamentGamesFilters = {
      status: GameStatuses.open,
      date_to: this.openGamesDateTo.toISOString().substring(0, 10),
    };
    if (this.tournamentId) {
      filters.tournamentIds = [this.tournamentId];
    }
    if (this.tournamentSeasonId) {
      filters.tournamentSeasonId = this.tournamentSeasonId;
    }
    const games = await this.leagueApi.getGames(this.leagueId, this.openGamesPage, this.size, filters);
    this.openGamesTotal = games.total;
    if (this.openGamesPage === 1) {
      this.store.save(STORE_GAMES_KEY, this.filterGames(games.data));
    } else {
      this.store.appendItems(STORE_GAMES_KEY, this.filterGames(games.data));
    }
    if (games.data.length < this.size) {
      this.closedGamesPage = 1;
      return [
        ...games.data,
        ...(await this.loadClosedGames())
      ];
    }
    return games.data;
  }

  async loadClosedGames(): Promise<Game[]> {
    const filters: TournamentGamesFilters = {statuses: [GameStatuses.closed, GameStatuses.in_progress]};
    if (this.tournamentId) {
      filters.tournamentIds = [this.tournamentId];
    }
    if (this.tournamentSeasonId) {
      filters.tournamentSeasonId = this.tournamentSeasonId;
    }
    const games = await this.leagueApi.getGames(
      this.leagueId,
      this.closedGamesPage,
      this.size,
      {...filters, status: GameStatuses.closed},
    );
    this.store.appendItems(STORE_GAMES_KEY, this.filterGames(games.data));
    return games.data;
  }

  async loadNextPage(): Promise<Game[]> {
    if (this.closedGamesPage > 0) {
      if (this.closedGamesPage * this.size >= this.closedGamesTotal) {
        return Promise.resolve([]);
      }
      this.closedGamesPage++;
      return await this.loadClosedGames();
    }
    if (this.openGamesPage * this.size >= this.openGamesTotal) {
      this.closedGamesPage++;
      return await this.loadClosedGames();
    }
    this.openGamesPage++;
    return await this.loadOpenGames();
  }

  private async getFirstGame(filters: TournamentGamesFilters): Promise<Game> {
    const games = await this.leagueApi.getGames(this.leagueId, 1, 1, filters);
    return games.data[0];
  }

  private listenCentrifugoEvents(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.centrifugoService.listen(`league_${this.leagueId}`)
     .subscribe(message => {
       switch (message.action) {
         case 'GAME_CLOSED':
         case 'GAME_UPDATED':
         case 'GAME_SCORE_UPDATED': {
           const newGame = Game.toFront(message.data);
           if ([GameStatuses.in_progress, GameStatuses.closed].indexOf(newGame.status) < 0) {
             return;
           }
           const game = (this.store.value(STORE_GAMES_KEY) || []).find(item => item.id === newGame.id);
           if (game) {
             return this.store.updateItem(STORE_GAMES_KEY, newGame);
           }
           return this.store.prependItem(STORE_GAMES_KEY, Game.toFront(message.data));
         }
       }
     });
  }

  private filterGames(games: Game[]): Game[] {
    return games.filter(g => EXCLUDE_GAMES_TOURNAMENT_ID.indexOf(g.tournament.id) === -1 && EXCLUDE_HEADLINE_GAMES.indexOf(g.id) === -1);
  }
}
